import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { marketing, owner as ownerApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Sparkles, Plus, Loader2, Trash2, ExternalLink, Copy, GripVertical, X, Users, MessageCircle } from 'lucide-react';
import { MarketingLocked } from './LandingPageBuilder';

// Survey Builder — owners generate an AI 5-question draft, edit, reorder,
// and share. Responses land in their contact list + the member's profile.
export default function SurveyBuilder() {
  const [surveys, setSurveys] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [locked, setLocked] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editor, setEditor] = useState(null);   // survey being edited (draft or persisted)
  const [genBusinessId, setGenBusinessId] = useState('');

  const load = async () => {
    try {
      const [s, b] = await Promise.all([marketing.listSurveys(), ownerApi.myBusinesses().catch(() => [])]);
      setSurveys(s); setBusinesses(b || []);
      if (!genBusinessId && b?.length) setGenBusinessId(b[0].id);
    } catch (e) {
      if (e?.response?.status === 403) setLocked(true);
      else toast({ title: 'Failed to load surveys' });
      setSurveys([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startGenerate = async () => {
    if (!genBusinessId) { toast({ title: 'Pick a business first' }); return; }
    setCreating(true);
    try {
      const r = await marketing.surveyGenerate(genBusinessId);
      const biz = businesses.find(b => b.id === genBusinessId);
      setEditor({
        _draft: true,
        businessId: genBusinessId,
        title: r.suggestedTitle || `${biz?.name || 'Customer'} survey`,
        description: '',
        questions: r.questions,
      });
      toast({ title: 'Draft generated', description: 'Edit as you like, then save.' });
    } catch (e) {
      toast({ title: 'Generate failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setCreating(false); }
  };

  const removeSurvey = async (s) => {
    if (!window.confirm(`Delete survey "${s.title}"?`)) return;
    try { await marketing.deleteSurvey(s.id); setSurveys(prev => prev.filter(x => x.id !== s.id)); toast({ title: 'Deleted' }); }
    catch { toast({ title: 'Delete failed' }); }
  };

  if (locked) return <MarketingLocked/>;
  if (surveys === null) return <div className="text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;

  return (
    <div className="space-y-4" data-testid="survey-builder-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold inline-flex items-center gap-2"><Sparkles size={22} className="text-yellow-400"/> Survey Builder</h1>
          <p className="text-gray-400 text-sm">AI drafts 5 questions tailored to your industry — edit, reorder, then share.</p>
        </div>
      </div>

      <div className="pbb-card p-4 flex items-center gap-2 flex-wrap" data-testid="survey-generate-row">
        <select value={genBusinessId} onChange={e => setGenBusinessId(e.target.value)}
          data-testid="survey-generate-business"
          className="bg-black border border-[#2a2a2a] rounded-lg px-3 py-2 text-sm text-gray-200">
          <option value="">Select business</option>
          {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <button onClick={startGenerate} disabled={creating || !genBusinessId}
          data-testid="survey-generate-btn"
          className="pbb-btn-green inline-flex items-center gap-2">
          {creating ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>} Generate 5 AI questions
        </button>
        <div className="text-[11px] text-gray-500">Uses your business category + description. You'll edit before publishing.</div>
      </div>

      {surveys.length === 0 && !editor ? (
        <div className="pbb-card p-8 text-center" data-testid="surveys-empty">
          <MessageCircle className="mx-auto text-gray-500" size={26}/>
          <div className="mt-2 font-semibold text-white">No surveys yet</div>
          <p className="text-sm text-gray-400 mt-1">Pick a business above and let AI draft your first survey.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="surveys-list">
          {surveys.map(s => (
            <li key={s.id} className="pbb-card p-4 flex items-center gap-3 flex-wrap" data-testid={`survey-row-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold truncate">{s.title}</div>
                <div className="text-[11px] text-gray-400">/s/{s.slug} · {s.questions?.length || 0} questions · {s.responseCount || 0} responses</div>
              </div>
              <a href={`/s/${s.slug}`} target="_blank" rel="noreferrer" data-testid={`survey-view-${s.slug}`}
                className="text-xs px-2 py-1 rounded border border-[#2a2a2a] hover:border-green-500 text-gray-200 inline-flex items-center gap-1">
                <ExternalLink size={11}/> View
              </a>
              <button onClick={async () => {
                  try { await navigator.clipboard.writeText(`${window.location.origin}/s/${s.slug}`); toast({ title: 'Link copied' }); }
                  catch { toast({ title: `${window.location.origin}/s/${s.slug}` }); }
                }} data-testid={`survey-copy-${s.id}`}
                className="text-xs px-2 py-1 rounded border border-yellow-500/40 text-yellow-200 hover:bg-yellow-500/10 inline-flex items-center gap-1">
                <Copy size={11}/> Copy link
              </button>
              <button onClick={() => setEditor(s)} data-testid={`survey-edit-${s.id}`}
                className="text-xs px-2 py-1 rounded border border-[#2a2a2a] hover:border-green-500 text-gray-200">Edit</button>
              <Link to={`/marketing/surveys/${s.id}/responses`} data-testid={`survey-responses-${s.id}`}
                className="text-xs px-2 py-1 rounded border border-[#2a2a2a] hover:border-green-500 text-gray-200 inline-flex items-center gap-1">
                <Users size={11}/> Responses
              </Link>
              <button onClick={() => removeSurvey(s)} data-testid={`survey-delete-${s.id}`}
                className="text-xs px-2 py-1 rounded border border-[#2a2a2a] hover:border-red-500 text-gray-400 hover:text-red-400 inline-flex items-center gap-1">
                <Trash2 size={11}/>
              </button>
            </li>
          ))}
        </ul>
      )}

      {editor && (
        <SurveyEditor draft={editor} onClose={() => setEditor(null)}
          onSaved={(saved) => {
            setSurveys(prev => {
              const idx = prev.findIndex(x => x.id === saved.id);
              return idx >= 0 ? prev.map(x => x.id === saved.id ? saved : x) : [saved, ...prev];
            });
            setEditor(null);
          }}/>
      )}
    </div>
  );
}

function SurveyEditor({ draft, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    title: draft.title || '', description: draft.description || '',
    questions: (draft.questions || []).map(q => ({ ...q })),
  }));
  const [saving, setSaving] = useState(false);
  const setQ = (i, patch) => setForm(p => ({ ...p, questions: p.questions.map((q, j) => j === i ? { ...q, ...patch } : q) }));
  const move = (i, dir) => setForm(p => {
    const arr = [...p.questions]; const j = i + dir;
    if (j < 0 || j >= arr.length) return p;
    [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...p, questions: arr };
  });
  const remove = (i) => setForm(p => ({ ...p, questions: p.questions.filter((_, j) => j !== i) }));
  const add = () => setForm(p => ({ ...p, questions: [...p.questions, { type: 'single_choice', prompt: 'New question', options: ['Yes', 'No'], required: false }] }));

  const save = async () => {
    if (!form.title.trim()) { toast({ title: 'Survey title is required' }); return; }
    if ((form.questions || []).length === 0) { toast({ title: 'Add at least one question' }); return; }
    setSaving(true);
    try {
      let saved;
      if (draft._draft) {
        saved = await marketing.createSurvey({
          businessId: draft.businessId, title: form.title.trim(),
          description: form.description, questions: form.questions,
        });
        toast({ title: 'Survey published' });
      } else {
        saved = await marketing.updateSurvey(draft.id, {
          title: form.title.trim(), description: form.description, questions: form.questions,
        });
        toast({ title: 'Survey updated' });
      }
      onSaved(saved);
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 p-4 overflow-y-auto" data-testid="survey-editor"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="bg-[#0d0d0d] border border-[#2a2a2a] rounded-2xl max-w-2xl w-full my-8">
        <div className="p-5 border-b border-[#2a2a2a] flex items-start justify-between gap-3">
          <div>
            <div className="text-white font-bold text-lg">{draft._draft ? 'Publish survey' : 'Edit survey'}</div>
            <div className="text-xs text-gray-400 mt-0.5">Members earn +50 loyalty points for each survey they complete.</div>
          </div>
          <button type="button" onClick={onClose} disabled={saving} data-testid="survey-editor-close"
            className="text-gray-400 hover:text-white p-1"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-4">
          <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            data-testid="survey-editor-title" placeholder="Survey title"
            className="pbb-input"/>
          <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            data-testid="survey-editor-description" placeholder="Optional description shown above the form"
            className="pbb-input"/>
          <div className="space-y-3">
            {form.questions.map((q, i) => (
              <div key={i} className="rounded-lg border border-[#2a2a2a] p-3 space-y-2" data-testid={`survey-editor-q-${i}`}>
                <div className="flex items-center gap-2">
                  <div className="inline-flex flex-col text-gray-500">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`survey-editor-q-up-${i}`}
                      className="hover:text-white disabled:opacity-30">▲</button>
                    <button type="button" onClick={() => move(i, +1)} disabled={i === form.questions.length - 1} data-testid={`survey-editor-q-down-${i}`}
                      className="hover:text-white disabled:opacity-30">▼</button>
                  </div>
                  <GripVertical size={12} className="text-gray-500"/>
                  <select value={q.type} onChange={e => setQ(i, { type: e.target.value, options: ['single_choice','multi_choice'].includes(e.target.value) ? (q.options || ['Option 1','Option 2']) : null })}
                    data-testid={`survey-editor-q-type-${i}`}
                    className="text-xs bg-black border border-[#2a2a2a] rounded px-2 py-1 text-gray-200">
                    <option value="single_choice">Single choice</option>
                    <option value="multi_choice">Multi choice</option>
                    <option value="rating">Rating (1-5)</option>
                    <option value="text">Free text</option>
                  </select>
                  <label className="text-[11px] text-gray-400 inline-flex items-center gap-1 ml-auto">
                    <input type="checkbox" checked={!!q.required} onChange={e => setQ(i, { required: e.target.checked })}
                      data-testid={`survey-editor-q-required-${i}`}
                      className="accent-green-500"/>
                    Required
                  </label>
                  <button type="button" onClick={() => remove(i)} data-testid={`survey-editor-q-remove-${i}`}
                    className="text-gray-400 hover:text-red-400"><Trash2 size={12}/></button>
                </div>
                <input value={q.prompt} onChange={e => setQ(i, { prompt: e.target.value })}
                  data-testid={`survey-editor-q-prompt-${i}`}
                  className="pbb-input" placeholder="Question prompt"/>
                {['single_choice','multi_choice'].includes(q.type) && (
                  <div className="space-y-1">
                    {(q.options || []).map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <input value={opt}
                          onChange={e => setQ(i, { options: q.options.map((x, k) => k === oi ? e.target.value : x) })}
                          data-testid={`survey-editor-q-${i}-opt-${oi}`}
                          className="pbb-input flex-1" placeholder={`Option ${oi + 1}`}/>
                        <button type="button" onClick={() => setQ(i, { options: q.options.filter((_, k) => k !== oi) })}
                          data-testid={`survey-editor-q-${i}-opt-remove-${oi}`}
                          className="text-gray-500 hover:text-red-400"><Trash2 size={12}/></button>
                      </div>
                    ))}
                    <button type="button" onClick={() => setQ(i, { options: [...(q.options || []), 'New option'] })}
                      data-testid={`survey-editor-q-${i}-opt-add`}
                      className="text-xs text-green-400 hover:text-green-300">+ add option</button>
                  </div>
                )}
              </div>
            ))}
            <button type="button" onClick={add} data-testid="survey-editor-add-question"
              className="text-xs text-green-400 hover:text-green-300 inline-flex items-center gap-1">
              <Plus size={12}/> Add question
            </button>
          </div>
        </div>
        <div className="p-5 pt-0 flex gap-2">
          <button type="button" onClick={onClose} disabled={saving} data-testid="survey-editor-cancel"
            className="flex-1 px-3 py-2 rounded-lg border border-[#2a2a2a] hover:border-gray-500 text-gray-200 text-sm">Cancel</button>
          <button type="button" onClick={save} disabled={saving} data-testid="survey-editor-save"
            className="flex-1 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white text-sm inline-flex items-center justify-center gap-1 disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin"/> : null} {draft._draft ? 'Publish survey' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
